// src/hooks/useStorySeen.ts
//
// Client-side seen/unseen state (no auth in this project). Persisted to
// localStorage and shared across mounted components via a tiny subscription
// so the tray ring updates the moment a story is viewed.

import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'champions:stories:seen';

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

let cache: Set<string> | null = null;
const listeners = new Set<() => void>();

function snapshot(): Set<string> {
  if (!cache) cache = read();
  return cache;
}

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function markStorySeen(id: string) {
  const next = new Set(snapshot());
  if (next.has(id)) return;
  next.add(id);
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify([...next]));
  } catch {
    /* ignore quota / private-mode errors */
  }
  emit();
}

/** Returns the set of seen story ids (reactive) + a helper to mark one seen. */
export function useStorySeen() {
  const seen = useSyncExternalStore(subscribe, snapshot, snapshot);
  const isSeen = useCallback((id: string) => seen.has(id), [seen]);
  return { seen, isSeen, markSeen: markStorySeen };
}
